import React, { useMemo } from 'react'
import { useStateValue } from '@State'
import { resolveSelections } from '../../../../hotspot/geometry'
import { parseDragDropAnswer } from './dragDropAnswer'

const statusAlert = {
  correct: 'alert-success',
  incorrect: 'alert-danger',
  neutral: 'alert-secondary',
}

const ALLOCATION_LABELS = {
  all: 'All correct required',
  one: 'Any one correct answer',
  points: 'Points per answer',
  correct: 'Any answer accepted',
  plus_minus: '+/\u2212 per selection',
  any: 'Points per correct selection',
}

function parseJsonAnswer(answer) {
  if (!answer) return null
  try { return JSON.parse(answer) } catch { return null }
}

function computeCounts(question) {
  const { correctAnswer, answer, type, isCorrect } = question
  if (!correctAnswer) return null

  switch (type) {
    case 'multiplechoice': {
      const correctSet = new Set(correctAnswer)
      const { viewtype } = question.data
      let selected
      if (!answer) selected = new Set()
      else if (viewtype === 'checkbox') selected = new Set(answer.split(',').filter(Boolean))
      else selected = new Set([answer])
      let correct = 0
      let incorrect = 0
      for (const guid of selected) {
        if (correctSet.has(guid)) correct++
        else incorrect++
      }
      // Also count missed correct options as incorrect
      for (const guid of correctSet) {
        if (!selected.has(guid)) incorrect++
      }
      const total = correct + incorrect
      return { numCorrect: correct, numIncorrect: incorrect, total }
    }

    case 'ngn_dropdown': {
      const selections = parseJsonAnswer(answer) || {}
      let correct = 0, total = 0
      for (const [fieldGuid, correctOptGuid] of Object.entries(correctAnswer)) {
        if (!correctOptGuid) continue
        total++
        if (selections[fieldGuid] === correctOptGuid) correct++
      }
      return { numCorrect: correct, numIncorrect: total - correct, total }
    }

    case 'ngn-dropdown-table': {
      const selections = parseJsonAnswer(answer) || {}
      let correct = 0, total = 0
      for (const [cellKey, correctOptGuid] of Object.entries(correctAnswer)) {
        if (!correctOptGuid) continue
        total++
        if (selections[cellKey] === correctOptGuid) correct++
      }
      return { numCorrect: correct, numIncorrect: total - correct, total }
    }

    case 'ngn-grouping': {
      const selections = parseJsonAnswer(answer) || {}
      const correctSet = new Set(correctAnswer)
      const total = correctSet.size
      let correct = 0
      for (const [optGuid, isSelected] of Object.entries(selections)) {
        if (isSelected && correctSet.has(optGuid)) correct++
      }
      return { numCorrect: correct, numIncorrect: total - correct, total }
    }

    case 'ngn_highlight': {
      const selected = parseJsonAnswer(answer) || []
      const correctSet = new Set(correctAnswer)
      const total = correctSet.size
      let correct = 0
      for (const guid of selected) {
        if (correctSet.has(guid)) correct++
      }
      return { numCorrect: correct, numIncorrect: total - correct, total }
    }

    case 'hotspot': {
      // Correct regions hit (once each) vs. correct regions missed plus every other selection.
      const correctSet = new Set(correctAnswer)
      const resolved = resolveSelections(question.data.regions, parseJsonAnswer(answer) || [])
      const hit = new Set(resolved.filter(r => r && correctSet.has(r.guid)).map(r => r.guid))
      const wrong = resolved.filter(r => !r || !correctSet.has(r.guid)).length
      return { numCorrect: hit.size, numIncorrect: correctSet.size - hit.size + wrong, total: correctSet.size }
    }

    case 'ngn-matrix': {
      const selections = parseJsonAnswer(answer) || {}
      let correct = 0, total = 0
      for (const [rowGuid, correctCols] of Object.entries(correctAnswer)) {
        for (const colNum of correctCols) {
          total++
          const key = `${rowGuid}-${colNum}`
          if (selections[key]) correct++
        }
      }
      return { numCorrect: correct, numIncorrect: total - correct, total }
    }

    case 'ngn-bowtie': {
      const parsed = parseJsonAnswer(answer) || {}
      const { bowtie } = question.data
      let correct = 0, total = 0
      const checkZone = (position, numSlots) => {
        const correctGuids = position === 'center'
          ? (correctAnswer.center ? [correctAnswer.center] : [])
          : (correctAnswer[position] || [])
        const answerObj = position === 'center'
          ? { 0: parsed.center || '' }
          : (parsed[position] || {})
        for (let i = 0; i < numSlots; i++) {
          total++
          if (correctGuids[i] && answerObj[i] === correctGuids[i]) correct++
        }
      }
      checkZone('left', bowtie.left.numCorrect)
      checkZone('center', bowtie.center.numCorrect)
      checkZone('right', bowtie.right.numCorrect)
      return { numCorrect: correct, numIncorrect: total - correct, total }
    }

    case 'ngn-dragdrop': {
      const parsed = parseDragDropAnswer(answer)
      let correct = 0, total = 0
      for (const [fieldId, correctGuids] of Object.entries(correctAnswer)) {
        if (!Array.isArray(correctGuids)) continue
        const answerGuids = (parsed[fieldId] || '').trim().split(' ').filter(Boolean)
        for (let i = 0; i < correctGuids.length; i++) {
          total++
          if (answerGuids[i] === correctGuids[i]) correct++
        }
      }
      return { numCorrect: correct, numIncorrect: total - correct, total }
    }

    case 'matching': {
      const selections = parseJsonAnswer(answer) || {}
      let correct = 0, total = 0
      for (const [itemGuid, correctOptionGuid] of Object.entries(correctAnswer)) {
        total++
        if (selections[itemGuid] === correctOptionGuid) correct++
      }
      return { numCorrect: correct, numIncorrect: total - correct, total }
    }

    default: {
      if (isCorrect === true) return { numCorrect: 1, numIncorrect: 0, total: 1 }
      if (isCorrect === false) return { numCorrect: 0, numIncorrect: 1, total: 1 }
      return null
    }
  }
}

export const FeedbackDisplay = ({ question, children }) => {
  const [state] = useStateValue()
  const { showFeedback } = state
  const { showPoints, showAnswer, feedbackDisplay } = state.feedbackSettings
  const { gradePoints, points, isCorrect, feedback, fullyAnswered } = question

  if (!showFeedback) return null

  const hasPoints = showPoints && gradePoints !== null && gradePoints !== undefined
  const showFeedbackText = feedbackDisplay === 'show'
    || (feedbackDisplay === 'incorrect' && isCorrect === false)
  const hasFeedbackText = showFeedbackText && !!feedback

  if (!fullyAnswered && !hasPoints && !hasFeedbackText) return null

  const alertClass = isCorrect !== null && isCorrect !== undefined
    ? (isCorrect ? statusAlert.correct : statusAlert.incorrect)
    : statusAlert.neutral

  const score = hasPoints ? `${gradePoints} / ${points}` : '—'
  const allocation = question.data && question.data.allocation
  const allocationLabel = allocation && ALLOCATION_LABELS[allocation]
  const counts = useMemo(
    () => showAnswer ? computeCounts(question) : null,
    [showAnswer, question.guid, question.correctAnswer, question.answer, question.type, question.isCorrect]
  )
  const numCorrect = counts ? counts.numCorrect : 0
  const numIncorrect = counts ? counts.numIncorrect : 0

  return (
    <div
      className={`card-myed mt-2  ml-0 ${alertClass}`}
      style={{ borderLeftWidth: 4, borderColor: 'black' }}
    >
      <header className="text-left text-muted" style={{ fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Feedback: </header>
      {allocationLabel && (
        <div className="text-left text-muted mb-1" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Grading: {allocationLabel}
        </div>
      )}
      {hasPoints && (
        <div className="d-flex" style={{ gap: '1.5rem' }}>
          <div>
            <div className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Score</div>
            <div className={`font-weight-bold ${isCorrect ? 'text-success' : isCorrect === false ? 'text-danger' : 'text-muted'}`} style={{ fontSize: '1.1rem' }}>{score}</div>
          </div>
          {counts && (
            <>
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Correct</div>
                <div className="font-weight-bold text-success" style={{ fontSize: '1.1rem' }}>{numCorrect}</div>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Incorrect</div>
                <div className="font-weight-bold text-danger" style={{ fontSize: '1.1rem' }}>{numIncorrect}</div>
              </div>
            </>
          )}
        </div>
      )}
      {fullyAnswered && children && <div className={hasPoints ? 'mt-2' : ''}>{children}</div>}
      {hasFeedbackText && (
        <div className={hasPoints || (fullyAnswered && children) ? 'mt-2 pt-2 border-top' : ''}>
          <div dangerouslySetInnerHTML={{ __html: feedback }} />
        </div>
      )}
    </div>
  )
}
